/**
 * Stress-minimising SGD phase (S_GD2, Zheng–Pawar–Goodman 2018) with
 * directed-flow constraints folded into the same annealed pair stream.
 *
 * Each update touches one pair:
 *   • stress pair — move both endpoints along their center chord so the
 *     distance closes toward the graph-target dᵢⱼ by µ = min(1, wᵢⱼ·η(t)).
 *   • flow pair — one-sided constraint x_to ≥ x_from + sep; when violated,
 *     shift the two x's apart by the µ-scaled violation.
 * η(t) anneals geometrically from η_max = d_max² (strongest pair starts at
 * µ = 1) down to η_min = ε·d_min² (every pair ends nearly frozen). Flow
 * terms share the schedule, so ordering is annealed INTO the layout rather
 * than fought over after the fact.
 *
 * Determinism: one seeded LCG; Fisher–Yates reshuffle of the merged term
 * array per epoch. Pinned nodes absorb no movement — their partner takes
 * the full correction; pinned-pinned terms are dropped at build time.
 *
 * The generator yields every `chunk` updates so the animation driver can
 * render the untangling continuously; the sync path drains it in one go.
 */

import type { Rect } from "./stressLayout.js";
import type { TargetPair } from "./graphDistances.js";

export const SGD_EPOCHS = 45;
export const SGD_MOVE_CAP = 120;
/** ε for η_min — how frozen the weakest pair ends. */
const SGD_EPS = 0.03;
/** Early exit: stop when an epoch's max single-node move drops below this. */
const SGD_CONVERGED_PX = 0.5;

export interface FlowTerm {
  /** Indexes into the ids array. Constraint: x[j] ≥ x[i] + sep. */
  readonly i: number;
  readonly j: number;
  readonly sep: number;
  readonly w: number;
}

interface SgdArgs {
  /** Sorted node ids of this component. */
  readonly ids: readonly string[];
  readonly pos: Map<string, { x: number; y: number }>;
  readonly size: ReadonlyMap<string, { w: number; h: number }>;
  readonly pinned: ReadonlyMap<string, Rect>;
  readonly pairs: readonly TargetPair[];
  readonly flow: readonly FlowTerm[];
  /** Deterministic seed (hash of the component's ids). */
  readonly seed: number;
  /** Yield granularity in pair updates. */
  readonly chunk: number;
}

/** FNV-1a — stable seed material from a component's id list. */
export function hashIds(ids: readonly string[]): number {
  let h = 2166136261 >>> 0;
  for (const id of ids) {
    for (let i = 0; i < id.length; i++) {
      h = Math.imul(h ^ id.charCodeAt(i), 16777619) >>> 0;
    }
    h = Math.imul(h ^ 0x7c, 16777619) >>> 0;
  }
  return h >>> 0;
}

type Term =
  | { kind: 0; p: TargetPair }
  | { kind: 1; f: FlowTerm };

export function* sgdPhase(args: SgdArgs): Generator<void, void, void> {
  const { ids, pos, size, pinned, chunk } = args;
  const n = ids.length;
  if (n < 2) return;

  const isPinned: boolean[] = ids.map((id) => pinned.has(id));
  const halfW: number[] = ids.map((id) => size.get(id)!.w / 2);
  const halfH: number[] = ids.map((id) => size.get(id)!.h / 2);
  const ps: { x: number; y: number }[] = ids.map((id) => pos.get(id)!);

  // Merged term list; pinned-pinned terms are useless — drop them now.
  const terms: Term[] = [];
  let dMin = Infinity;
  let dMax = 0;
  for (const p of args.pairs) {
    if (isPinned[p.i] && isPinned[p.j]) continue;
    terms.push({ kind: 0, p });
    if (p.d < dMin) dMin = p.d;
    if (p.d > dMax) dMax = p.d;
  }
  for (const f of args.flow) {
    if (isPinned[f.i] && isPinned[f.j]) continue;
    terms.push({ kind: 1, f });
  }
  if (terms.length === 0 || !Number.isFinite(dMin)) return;

  // Annealing schedule over the total epoch count.
  const etaMax = dMax * dMax;
  const etaMin = SGD_EPS * dMin * dMin;
  const decay = SGD_EPOCHS <= 1 ? 1 : Math.pow(etaMin / etaMax, 1 / (SGD_EPOCHS - 1));

  // LCG (numerical recipes) — the only randomness source; fully seeded.
  let lcg = args.seed >>> 0;
  const nextRand = (): number => {
    lcg = (Math.imul(lcg, 1664525) + 1013904223) >>> 0;
    return lcg / 4294967296;
  };

  const cap = (v: number): number =>
    v > SGD_MOVE_CAP ? SGD_MOVE_CAP : v < -SGD_MOVE_CAP ? -SGD_MOVE_CAP : v;

  let eta = etaMax;
  let sinceYield = 0;
  for (let epoch = 0; epoch < SGD_EPOCHS; epoch++) {
    // Fisher–Yates reshuffle each epoch.
    for (let k = terms.length - 1; k > 0; k--) {
      const r = Math.floor(nextRand() * (k + 1));
      const tmp = terms[k]!;
      terms[k] = terms[r]!;
      terms[r] = tmp;
    }

    let maxMove = 0;
    for (const term of terms) {
      if (term.kind === 0) {
        const { i, j, d, w } = term.p;
        const mu = Math.min(1, w * eta);
        const a = ps[i]!;
        const b = ps[j]!;
        const acx = a.x + halfW[i]!;
        const acy = a.y + halfH[i]!;
        const bcx = b.x + halfW[j]!;
        const bcy = b.y + halfH[j]!;
        let dx = bcx - acx;
        let dy = bcy - acy;
        let dist = Math.hypot(dx, dy);
        if (dist < 1e-6) {
          // Coincident centers — deterministic unstick along x by id order.
          dx = 1e-3;
          dy = 0;
          dist = 1e-3;
        }
        const r = (mu * (dist - d)) / (2 * dist); // >0 → too far, pull together
        const rawX = r * dx;
        const rawY = r * dy;
        const iPin = isPinned[i]!;
        const jPin = isPinned[j]!;
        // SGD_MOVE_CAP bounds the APPLIED per-node delta — the pinned-
        // partner transfer doubles the raw correction, so cap after
        // doubling, not before.
        let m: number;
        if (iPin) {
          const mvX = cap(2 * rawX);
          const mvY = cap(2 * rawY);
          b.x -= mvX;
          b.y -= mvY;
          m = Math.hypot(mvX, mvY);
        } else if (jPin) {
          const mvX = cap(2 * rawX);
          const mvY = cap(2 * rawY);
          a.x += mvX;
          a.y += mvY;
          m = Math.hypot(mvX, mvY);
        } else {
          const mvX = cap(rawX);
          const mvY = cap(rawY);
          a.x += mvX;
          a.y += mvY;
          b.x -= mvX;
          b.y -= mvY;
          m = Math.hypot(mvX, mvY);
        }
        if (m > maxMove) maxMove = m;
      } else {
        const { i, j, sep, w } = term.f;
        const a = ps[i]!;
        const b = ps[j]!;
        const acx = a.x + halfW[i]!;
        const bcx = b.x + halfW[j]!;
        const v = sep - (bcx - acx);
        if (v <= 0) continue;
        const mu = Math.min(1, w * eta);
        const rawShift = (mu * v) / 2;
        const iPin = isPinned[i]!;
        const jPin = isPinned[j]!;
        let m: number;
        if (iPin) {
          const shift = cap(2 * rawShift);
          b.x += shift;
          m = Math.abs(shift);
        } else if (jPin) {
          const shift = cap(2 * rawShift);
          a.x -= shift;
          m = Math.abs(shift);
        } else {
          const shift = cap(rawShift);
          a.x -= shift;
          b.x += shift;
          m = Math.abs(shift);
        }
        if (m > maxMove) maxMove = m;
      }

      sinceYield++;
      if (sinceYield >= chunk) {
        sinceYield = 0;
        yield;
      }
    }

    eta *= decay;
    if (epoch >= 5 && maxMove < SGD_CONVERGED_PX) break;
  }
  if (sinceYield > 0) yield;
}
